import axios from 'axios';

import { ENTITY_DOC_UPLOAD_PLATFORM, IS_ENTITY_DOC_PLATFORM_ENABLED } from '../constants/accountType.constants';
import { getEntityDocPreSignedUrls } from '../services/authApi/userApi';
import { getIndexedEntityDocFileName } from './fileValidation';

// Per-document upload states reported through the onStatusChange callback.
export const ENTITY_DOC_UPLOAD_STATUS = {
  WAITING: 'waiting',
  UPLOADING: 'uploading',
  DONE: 'done',
  ERROR: 'error',
};

// Builds the `files` array sent to POST /api/v1/documents/pre-sign-urls — one entry per added
// document, with the docType-indexed file name (AOI1.png, AOI2.png, ...) computed per docType.
export const buildEntityDocFilesPayload = (addedDocuments) => {
  const docTypeCounters = {};

  return addedDocuments.map((documentItem) => {
    const fileType = documentItem.fileName?.split('.')?.pop()?.toLowerCase();
    const fileName = getIndexedEntityDocFileName(documentItem.docTypeShortCode, documentItem.fileName, docTypeCounters);
    return { docType: documentItem.docTypeValue, fileType, fileName };
  });
};

// Requests pre-signed URLs for every added document in a single batched call.
// Response is expected to mirror the request order — one { url, storagePath } per file.
export const requestEntityDocPreSignedUrls = async (files) => {
  const resp = await getEntityDocPreSignedUrls(files, IS_ENTITY_DOC_PLATFORM_ENABLED ? ENTITY_DOC_UPLOAD_PLATFORM : undefined);
  return resp?.data?.files ?? resp?.data ?? [];
};

// Uploads each added document to its pre-signed S3 URL and returns the entityDocs array
// ({ docType, docUrl }) to send on profile update. onStatusChange(uid, { state, percent })
// reports each file's progress so the UI can show Waiting / % / done per document.
export const uploadEntityDocumentsToStorage = async (addedDocuments, preSignedUrls, onStatusChange = () => {}) => {
  if (preSignedUrls.length !== addedDocuments.length) {
    throw new Error('Invalid pre-signed URL response: mismatched file count');
  }

  const uploadedDocs = [];

  for (let index = 0; index < addedDocuments.length; index += 1) {
    const documentItem = addedDocuments[index];
    const fileType = documentItem.fileName?.split('.')?.pop()?.toLowerCase();
    const preSignedUrl = preSignedUrls[index]?.url;
    const storagePath = preSignedUrls[index]?.storagePath;

    if (!preSignedUrl || !storagePath) {
      throw new Error('Invalid pre-signed URL response: missing URL or storage path');
    }

    onStatusChange(documentItem.uid, { state: ENTITY_DOC_UPLOAD_STATUS.UPLOADING, percent: 0 });
    try {
      await axios.put(preSignedUrl, documentItem.originFileObj, {
        headers: { 'Content-Type': fileType },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          // Hold at 99% until S3 confirms, so 100% always coincides with the green check.
          const percent = Math.min(99, Math.round((progressEvent.loaded / progressEvent.total) * 100));
          onStatusChange(documentItem.uid, { state: ENTITY_DOC_UPLOAD_STATUS.UPLOADING, percent });
        },
      });
    } catch (error) {
      onStatusChange(documentItem.uid, { state: ENTITY_DOC_UPLOAD_STATUS.ERROR });
      throw error;
    }
    onStatusChange(documentItem.uid, { state: ENTITY_DOC_UPLOAD_STATUS.DONE, percent: 100 });
    uploadedDocs.push({ docType: documentItem.docTypeValue, docUrl: storagePath });
  }

  return uploadedDocs;
};

// Orchestrates the full entity document upload flow: build the batched pre-sign request,
// fetch all pre-signed URLs in one call, upload each file, and return the entityDocs array
// to send on profile update. Returns [] when there's nothing to upload.
export const uploadEntityDocuments = async (addedDocuments, onStatusChange = () => {}) => {
  if (!addedDocuments.length) {
    return [];
  }

  const files = buildEntityDocFilesPayload(addedDocuments);
  const preSignedUrls = await requestEntityDocPreSignedUrls(files);
  return uploadEntityDocumentsToStorage(addedDocuments, preSignedUrls, onStatusChange);
};
